import React, { useState, useEffect } from 'react';
import { Button, Alert, Spinner, Form } from '@/components/ui';
import { Task, TaskStage } from '../types/Task';
import { taskService } from '../services/taskService';
import ResponsiveModal from '../features/quantified-tracking/components/ResponsiveModal';
import { projectService } from '../services/projectService';
import { activityService } from '../services/activityService';
import { Activity } from '../types/Activity';
import { Project } from '../types/Project';
import { getFieldLockingState, getLockedFieldStyle, canEditInternalReview } from '../utils/fieldLocking';
import { usePermissions } from '../hooks/usePermissions';

// Utility function to format text in sentence case
const formatSentenceCase = (text: string): string => {
  if (!text) return '';
  // Handle underscore case first (e.g., "some_text" -> "Some Text")
  const withoutUnderscores = text.replace(/_/g, ' ');
  // Split into words, capitalize first letter of each word
  return withoutUnderscores
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

interface OverheadTaskModalProps {
  visible: boolean;
  onClose: () => void;
  onTaskSaved: (task: Task) => void;
  task?: Task;
  projectId?: number;  // Make projectId optional
  defaultStatus?: string;
}

const OverheadTaskModal: React.FC<OverheadTaskModalProps> = ({
  visible,
  onClose,
  onTaskSaved,
  task,
  projectId: initialProjectId,
  defaultStatus
}) => {
  const { hasPermission } = usePermissions();
  const canEditInternalReviewPermission = canEditInternalReview(hasPermission);
  const fieldLockingState = getFieldLockingState({
    taskStatus: task?.status as TaskStage,
    hasEditInternalReviewPermission: canEditInternalReviewPermission
  });
  const isTaskLocked = fieldLockingState.isLocked;
  const lockedFieldStyle = getLockedFieldStyle(isTaskLocked);

  const [description, setDescription] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED'>(
    (defaultStatus as 'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED') || 'ACTIVE'
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | undefined>(initialProjectId);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedActivityId, setSelectedActivityId] = useState<number | ''>('');

  useEffect(() => {
    if (task) {
      setDescription(task.description || '');
      setName(task.name || '');
      setStatus(task.status as 'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED');
      setSelectedActivityId(task.activityId || '');
      setSelectedProjectId(task.projectId);
    } else {
      setDescription('');
      setName('');
      setStatus((defaultStatus as 'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED') || 'ACTIVE');
      setSelectedActivityId('');
      setSelectedProjectId(initialProjectId);
    }
  }, [task, initialProjectId, defaultStatus]);

  useEffect(() => {
    if (!visible) return;
    const loadProjects = async () => {
      try {
        const fetchedProjects = await projectService.getProjectsForTaskModal(undefined, 'active,planned');
        setProjects(Array.isArray(fetchedProjects) ? fetchedProjects : []);
      } catch (err) {
        console.error('Error loading projects:', err);
        setError('Failed to load projects');
        setProjects([]);
      }
    };
    loadProjects();
  }, [visible]);

  useEffect(() => {
    const loadActivities = async () => {
      try {
        const fetchedActivities = await activityService.getActivitiesByClassification('overhead');
        setActivities(fetchedActivities);
      } catch (error) {
        console.error('Error loading activities:', error);
      }
    };
    loadActivities();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    if (!selectedProjectId) {
      setError('Please select a project');
      setIsSubmitting(false);
      return;
    }

    if (!selectedActivityId) {
      setError('Please select an activity');
      setIsSubmitting(false);
      return;
    }

    if (!name.trim()) {
      setError('Please enter a task name');
      setIsSubmitting(false);
      return;
    }

    try {
      const taskData = {
        description,
        name,
        type: 'Generic',
        projectId: selectedProjectId,
        status,
        taskLocations: [],
        activityId: selectedActivityId || undefined,
        isOverhead: true
      };

      let savedTask;
      if (task) {
        savedTask = await taskService.updateTask(task.id.toString(), taskData);
      } else {
        savedTask = await taskService.createTask(taskData);
      }

      onTaskSaved(savedTask);
      onClose();
    } catch (err) {
      setError('Failed to save task. Please try again.');
      console.error('Error saving task:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      isOpen={visible}
      onClose={onClose}
      title={task ? (isTaskLocked ? 'View Overhead Task' : 'Edit Overhead Task') : 'New Overhead Task'}
      readOnly={Boolean(task) && isTaskLocked}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {isTaskLocked ? 'Close' : 'Cancel'}
          </Button>
          {/* V2-074 / EDIT-03: a locked task has no Save – re-open is the only way to edit. */}
          {!isTaskLocked && (
            <Button
              type="submit"
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedProjectId || !selectedActivityId || !name.trim()}
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="tw-mr-2" />
                  Saving...
                </>
              ) : (
                'Save Task'
              )}
            </Button>
          )}
        </>
      }
    >
      {error && (
        <Alert variant="danger">
          {error}
        </Alert>
      )}
      <div className="tw-mb-3">
        <Form.Label htmlFor="project">Project <span className="tw-text-danger">*</span></Form.Label>
        {isTaskLocked ? (
          <Form.Control
            id="project"
            type="text"
            readOnly
            value={projects.find(p => p.id === selectedProjectId)?.name ?? ''}
            style={lockedFieldStyle}
          />
        ) : (
          <Form.Select
            id="project"
            value={selectedProjectId || ''}
            onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : undefined)}
          >
            <option value="">Select Project</option>
            {projects.map(proj => (
              <option key={proj.id} value={proj.id}>
                {proj.name} {proj.projectNumber ? `(${proj.projectNumber})` : ''}
              </option>
            ))}
          </Form.Select>
        )}
      </div>
      <div className="tw-mb-3">
        <Form.Label htmlFor="activity">Activity <span className="tw-text-danger">*</span></Form.Label>
        <Form.Select
          id="activity"
          value={selectedActivityId}
          onChange={(e) => setSelectedActivityId(e.target.value ? Number(e.target.value) : '')}
        >
          <option value="">Select Activity</option>
          {activities.map(activity => (
            <option key={activity.id} value={activity.id}>
              {activity.activityNumber} - {activity.activityName}
            </option>
          ))}
        </Form.Select>
        <small className="tw-text-content-muted">
          This is an overhead activity for tracking general project tasks
        </small>
      </div>
      <div className="tw-mb-3">
        <Form.Label htmlFor="name">Task Name <span className="tw-text-danger">*</span></Form.Label>
        <Form.Control
          id="name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter task name..."
        />
        <small className="tw-text-content-muted">
          Enter a descriptive name for this task
        </small>
      </div>
      <div className="tw-mb-3">
        <Form.Label htmlFor="status">Status</Form.Label>
        <Form.Select
          id="status"
          value={status}
          onChange={(e) => setStatus(e.target.value as 'PROPOSED' | 'PLANNED' | 'ACTIVE' | 'CLOSED' | 'CANCELLED')}
        >
          <option value="PROPOSED">{formatSentenceCase('PROPOSED')}</option>
          <option value="PLANNED">{formatSentenceCase('PLANNED')}</option>
          <option value="ACTIVE">{formatSentenceCase('ACTIVE')}</option>
          <option value="CLOSED">{formatSentenceCase('CLOSED')}</option>
          <option value="CANCELLED">{formatSentenceCase('CANCELLED')}</option>
        </Form.Select>
      </div>
      <div className="tw-mb-3">
        <Form.Label htmlFor="description">
          Internal Notes / Description (Optional)
        </Form.Label>
        <Form.Control
          as="textarea"
          id="description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="Enter internal notes or description..."
        />
      </div>
    </ResponsiveModal>
  );
};

export default OverheadTaskModal; 